/**
 * SurfaceAnchor — shared recognizer for canonical header / footer
 * surface rows ("anchors"). An anchor is THE row that represents a
 * sheet surface: `peekHeaderRow()` / `peekFooterRow()` resolve to it,
 * its subtree is the frozen / footer region, and projection-hoisted
 * children graft onto it.
 *
 * Three shapes count, in tie-break priority order:
 *
 *   1. `source: "server"` — server-backed surface container.
 *      `InertRow` with `descriptor.kind === "surface-container"` and
 *      server-stamped `RowMetadata.meta.surface = "header" | "footer"`.
 *   2. `source: "authored"` — template/user-authored surface region.
 *      `InertRow` with `descriptor.kind === "surface"` carrying
 *      `attributes.surface = "header" | "footer"`. The surface row IS
 *      the region; its children stay in-tree.
 *   3. `source: "legacy"` — pre-Phase-D synthetic placeholder.
 *      `InertRow` of `kind === "header"` / `"sheet-footer"` sitting at
 *      top level directly (not yet wrapped under a surface container).
 *
 * Bare / `surface: "body"` surface rows are NOT anchors — body is
 * always the implicit singleton surface. They keep their inline
 * (CSS-class-only) rendering.
 */

import type { Option } from "../core/option.ts";
import { Json } from "../core/json.ts";
import { InertRow } from "../rows/variants/inert-row.ts";
import type { ModelRow } from "../rows/model-row.ts";

export namespace SurfaceAnchor {
  export type Kind = "header" | "footer";
  export type Source = "server" | "authored" | "legacy";

  export interface Anchor {
    readonly kind: Kind;
    readonly source: Source;
  }

  /**
   * Resolve `row`'s anchor shape, or `null` when the row is not a
   * surface anchor (including bare/body surface rows).
   */
  export function of(row: ModelRow): Option<Anchor> {
    if (!(row instanceof InertRow)) return null;
    const inertKind = row.descriptor.kind;
    if (inertKind === "surface-container") {
      const surface = row.sheetReads.rowMeta(row.id).meta?.surface;
      if (surface === "header" || surface === "footer") {
        return { kind: surface, source: "server" };
      }
      return null;
    }
    if (inertKind === "surface") {
      const surface = Json.string(row.attributes(), "surface");
      if (surface === "header" || surface === "footer") {
        return { kind: surface, source: "authored" };
      }
      return null;
    }
    if (inertKind === "header") return { kind: "header", source: "legacy" };
    if (inertKind === "sheet-footer") return { kind: "footer", source: "legacy" };
    return null;
  }
}
